import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiCheck, FiChevronRight, FiLock, FiUsers } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { AUTH_API_URL, CRM_API_URL } from '../config';
import WorkspaceDialog from './WorkspaceDialog';
import InterfaceLanguageSelect from './InterfaceLanguageSelect';
import { useWhatsAppAccess } from './WhatsAppAccess';

const USER_PROFILE_OPTIONS = [
  'farmer', 'cooperative', 'financial_institution', 'field_advisor',
  'agribusiness', 'public_development_actor', 'other',
];

export default function Account() {
  const { t } = useTranslation('workspace');
  const { linked, phone, openWhatsApp } = useWhatsAppAccess();
  const [profile, setProfile] = useState(null);
  const [names, setNames] = useState({ firstName: '', lastName: '' });
  // UI draft only: the existing profile endpoint does not accept this field yet.
  const [userProfile, setUserProfile] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [crmAccess, setCrmAccess] = useState(false);
  const token = localStorage.getItem('token');
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    fetch(`${CRM_API_URL}/api/crm/access`, {
      headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
    }).then((response) => { if (response.ok) setCrmAccess(true); }).catch(() => {});
    return () => controller.abort();
  }, [token]);
  useEffect(() => {
    let active = true;
    setLoading(true); setLoadError(false);
    axios.get(`${AUTH_API_URL}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!active) return;
        setProfile(data);
        setNames({ firstName: data.firstName || '', lastName: data.lastName || '' });
      })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [token, retry]);

  const changed = profile && Object.keys(names).some((key) => names[key].trim() !== (profile[key] || ''));
  const saveProfile = async (event) => {
    event.preventDefault();
    if (!changed || saving) return;
    const next = { firstName: names.firstName.trim(), lastName: names.lastName.trim() };
    // Empty values are ignored by the existing endpoint; never claim they were saved.
    if ((profile.firstName && !next.firstName) || (profile.lastName && !next.lastName)) { setFeedback('nameRequired'); return; }
    const payload = Object.fromEntries(Object.entries(next).filter(([key, value]) => value && value !== (profile[key] || '')));
    if (!Object.keys(payload).length) return;
    setSaving(true); setFeedback('');
    try {
      await axios.put(`${AUTH_API_URL}/update-profile`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setProfile((previous) => ({ ...previous, ...payload })); setNames(next); setFeedback('saved');
    } catch { setFeedback('error'); }
    finally { setSaving(false); }
  };
  const whatsappLinked = linked ?? profile?.linked_channels?.whatsapp ?? null;
  const whatsappPhone = phone || profile?.phone_e164;
  const changeName = (event) => { setNames({ ...names, [event.target.name]: event.target.value }); setFeedback(''); };

  return <div className="ws-account-page">
    <Link className="ws-account-back" to="/chat"><FiArrowLeft aria-hidden="true" />{t('accountBack')}</Link>
    <header className="ws-account-heading"><h1>{t('account')}</h1><p>{t('accountIntro')}</p></header>
    {loading ? <p className="ws-account-loading" role="status">{t('loading')}</p> : loadError ? <div className="ws-account-loading" role="alert"><p>{t('loadError')}</p><button type="button" className="ws-secondary" onClick={() => setRetry((value) => value + 1)}>{t('retry')}</button></div> : <>
      <section className="ws-account-section" aria-labelledby="account-profile-heading">
        <div className="ws-account-section-title"><h2 id="account-profile-heading">{t('accountInformation')}</h2></div>
        <form className="ws-account-section-content" onSubmit={saveProfile}>
          <div className="ws-account-names">
            <label className="ws-field">{t('accountFirstName')}<input name="firstName" autoComplete="given-name" value={names.firstName} onChange={changeName} disabled={saving} required={Boolean(profile.firstName)} /></label>
            <label className="ws-field">{t('accountLastName')}<input name="lastName" autoComplete="family-name" value={names.lastName} onChange={changeName} disabled={saving} required={Boolean(profile.lastName)} /></label>
          </div>
          <div className="ws-account-email"><span>{t('accountEmail')}</span><p>{profile.email || t('accountNoEmail')}</p></div>
          <div className="ws-account-save"><button type="submit" className="ws-primary" disabled={!changed || saving}>{t(saving ? 'saving' : 'save')}</button>
            {feedback && <p className={`ws-setting-feedback${feedback !== 'saved' ? ' ws-error' : ''}`} role={feedback === 'saved' ? 'status' : 'alert'}>{feedback === 'saved' && <FiCheck aria-hidden="true" />}{t(feedback === 'saved' ? 'accountProfileSaved' : feedback === 'nameRequired' ? 'accountNameRequired' : 'saveError')}</p>}
          </div>
        </form>
      </section>
      <section className="ws-account-section" aria-labelledby="account-user-profile-heading">
        <div className="ws-account-section-title"><h2 id="account-user-profile-heading">{t('userProfileTitle')}</h2></div>
        <div className="ws-account-section-content">
          <div className="ws-field">
            <select value={userProfile} onChange={(event) => setUserProfile(event.target.value)}
              aria-labelledby="account-user-profile-heading" aria-describedby={userProfile ? 'account-user-profile-note' : undefined}>
              <option value="">{t('userProfilePlaceholder')}</option>
              {USER_PROFILE_OPTIONS.map((value) => <option key={value} value={value}>{t(`userProfile_${value}`)}</option>)}
            </select>
          </div>
          {userProfile && <p id="account-user-profile-note" className="ws-field-hint" role="status">{t('userProfileNotSaved')}</p>}
        </div>
      </section>
      <section className="ws-account-section" aria-labelledby="account-preferences-heading">
        <div className="ws-account-section-title"><h2 id="account-preferences-heading">{t('accountPreferences')}</h2></div>
        <div className="ws-account-section-content ws-account-preferences">
          <InterfaceLanguageSelect persist showHint />
        </div>
      </section>
      <section className="ws-account-section" aria-labelledby="account-access-heading">
        <div className="ws-account-section-title"><h2 id="account-access-heading">{t('accountAccess')}</h2></div>
        <div className="ws-account-section-content">
          <button className="ws-account-access-row" type="button" onClick={openWhatsApp} aria-haspopup="dialog"><FaWhatsapp aria-hidden="true" /><span><strong>WhatsApp</strong><small>{whatsappLinked === true ? (whatsappPhone || t('accountWhatsappLinked')) : whatsappLinked === false ? t('whatsappConnect') : t('accountWhatsappManage')}</small></span>{whatsappLinked === true && <FiCheck className="ws-account-connected-icon" aria-label={t('accountWhatsappLinked')} />}<FiChevronRight aria-hidden="true" /></button>
          <button className="ws-account-access-row" type="button" onClick={() => { setPasswordOpen(true); setPasswordSaved(false); }} aria-haspopup="dialog"><FiLock aria-hidden="true" /><span><strong>{t('accountPassword')}</strong><small>{t('accountPasswordChange')}</small></span><FiChevronRight aria-hidden="true" /></button>
          {crmAccess && <Link className="ws-account-access-row" to="/crm"><FiUsers aria-hidden="true" /><span><strong>CRM UpyaChat</strong><small>Utilisateurs, conversations et offres</small></span><FiChevronRight aria-hidden="true" /></Link>}
          {passwordSaved && <p className="ws-setting-feedback" role="status"><FiCheck aria-hidden="true" />{t('accountPasswordSaved')}</p>}
        </div>
      </section>
    </>}
    {passwordOpen && <PasswordDialog token={token} onClose={() => setPasswordOpen(false)} onSaved={() => { setPasswordOpen(false); setPasswordSaved(true); }} />}
  </div>;
}

function PasswordDialog({ token, onClose, onSaved }) {
  const { t } = useTranslation('workspace');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (password.length < 6) { setError('accountPasswordLength'); return; }
    if (password !== confirmation) { setError('accountPasswordMismatch'); return; }
    setSaving(true); setError('');
    try {
      await axios.put(`${AUTH_API_URL}/update-profile`, { password }, { headers: { Authorization: `Bearer ${token}` } });
      setPassword(''); setConfirmation(''); onSaved();
    } catch { setError('saveError'); }
    finally { setSaving(false); }
  };
  return <WorkspaceDialog title={t('accountPasswordChange')} onClose={() => { if (!saving) onClose(); }} className="ws-account-password-dialog">
    <form className="ws-form" onSubmit={submit}>
      <div><label className="ws-field">{t('accountNewPassword')}<input type="password" autoComplete="new-password" aria-describedby="account-password-hint" value={password} onChange={(event) => { setPassword(event.target.value); setError(''); }} minLength={6} required disabled={saving} /></label><p id="account-password-hint" className="ws-field-hint">{t('accountPasswordLength')}</p></div>
      <label className="ws-field">{t('accountConfirmPassword')}<input type="password" autoComplete="new-password" value={confirmation} onChange={(event) => { setConfirmation(event.target.value); setError(''); }} minLength={6} required disabled={saving} /></label>
      {error && <p className="ws-error" role="alert">{t(error)}</p>}
      <div className="ws-form-actions"><button type="button" className="ws-secondary" onClick={onClose} disabled={saving}>{t('cancel')}</button><button type="submit" className="ws-primary" disabled={saving || !password || !confirmation}>{t(saving ? 'saving' : 'save')}</button></div>
    </form>
  </WorkspaceDialog>;
}
